/**
 * RetailVitamin Pro v1.0 - DSHEA Hints UI Helper
 * Non-authoritative client-side content checker
 * Server-side validation (rv-webhook) is authoritative
 * Contract: FDA DSHEA 21 CFR 101.93, compliance_audit_entry_v1
 * Performance: < 5KB, runs on product/collection pages only
 */

(function() {
  'use strict';

  /* ==========================================================================
     RISKY TERMS DICTIONARY
     ========================================================================== */

  const RISKY_TERMS = [
    // Disease Claims (Prohibited under DSHEA)
    /\b(cure|treat|diagnose|prevent|therapy|therapeutic)\b/gi,

    // Specific Diseases (FDA Red Flags)
    /\b(cancer|tumor|diabetes|alzheimer|covid|coronavirus|virus|infection|disease|illness)\b/gi,

    // Medical Language (Requires Substantiation)
    /\b(medical|clinical|doctor|prescription|pharmaceutical)\b/gi,

    // Cardiovascular Claims (High Scrutiny)
    /\b(heart attack|stroke|blood pressure|cholesterol|hypertension)\b/gi,

    // FDA Structure/Function Boundary Terms
    /\b(boost immunity|immune support|detox|cleanse)\b/gi
  ];

  const WARNING_MESSAGES = {
    cure: 'Use "support" instead of "cure"',
    treat: 'Use "supports" instead of "treats"',
    diagnose: 'Remove diagnostic language',
    prevent: 'Use "supports" instead of "prevents"',
    disease: 'Avoid specific disease names',
    cancer: 'Prohibited disease claim',
    covid: 'Prohibited pandemic claim',
    medical: 'Avoid medical terminology'
  };

  /* ==========================================================================
     INITIALIZATION
     ========================================================================== */

  document.addEventListener('DOMContentLoaded', function() {
    // Only run if DSHEA hints are enabled in theme settings
    const dsheaEnabled = document.body.dataset.dsheaHintsEnabled !== 'false';
    if (!dsheaEnabled) {
      console.log('DSHEA hints disabled in theme settings');
      return;
    }

    // Only run on product/collection pages (skip cart/checkout)
    const isProductPage = document.body.classList.contains('template-product');
    const isCollectionPage = document.body.classList.contains('template-collection');

    if (!isProductPage && !isCollectionPage) {
      return;
    }

    scanPageContent();
    console.log('DSHEA hints initialized (UI helper only - server validation authoritative)');
  });

  /* ==========================================================================
     CONTENT SCANNING
     ========================================================================== */

  /**
   * Scan all elements with [data-dshea-check] attribute
   */
  function scanPageContent() {
    const elementsToCheck = document.querySelectorAll('[data-dshea-check]');

    elementsToCheck.forEach(element => {
      highlightRiskyTerms(element);
    });
  }

  /**
   * Highlight risky terms in element
   * @param {HTMLElement} element - Element to scan
   */
  function highlightRiskyTerms(element) {
    const text = element.textContent;
    const risks = [];
    const detectedTerms = [];

    RISKY_TERMS.forEach(regex => {
      const matches = text.match(regex);
      if (matches) {
        matches.forEach(match => {
          if (!detectedTerms.includes(match.toLowerCase())) {
            detectedTerms.push(match.toLowerCase());
            risks.push({
              term: match,
              suggestion: WARNING_MESSAGES[match.toLowerCase()] || 'Review for DSHEA compliance'
            });
          }
        });
      }
    });

    if (risks.length > 0) {
      applyWarningStyles(element, risks);
      logWarning(element, risks);
    }
  }

  /**
   * Apply visual warning styles
   * @param {HTMLElement} element - Element to style
   * @param {Array} risks - Array of risk objects
   */
  function applyWarningStyles(element, risks) {
    element.classList.add('dshea-warning');
    element.style.backgroundColor = '#fff3cd';
    element.style.borderLeft = '4px solid #f0ad4e';
    element.style.paddingLeft = '12px';
    element.style.paddingTop = '8px';
    element.style.paddingBottom = '8px';
    element.style.marginBottom = '16px';

    // Add tooltip with suggestions
    const tooltip = createTooltip(risks);
    element.setAttribute('data-risks', JSON.stringify(risks));
    element.setAttribute('title', tooltip);

    // Add warning icon
    const icon = document.createElement('span');
    icon.className = 'dshea-warning-icon';
    icon.innerHTML = '⚠️ ';
    icon.style.marginRight = '8px';
    icon.style.fontWeight = 'bold';
    element.prepend(icon);
  }

  /**
   * Create tooltip text with suggestions
   * @param {Array} risks - Array of risk objects
   * @returns {string} Tooltip text
   */
  function createTooltip(risks) {
    const terms = risks.map(r => r.term).join(', ');
    return `DSHEA Warning: Detected risky terms [${terms}]. Server validation required before publishing.`;
  }

  /**
   * Log warning to console
   * @param {HTMLElement} element - Element with warning
   * @param {Array} risks - Array of risk objects
   */
  function logWarning(element, risks) {
    console.group('🔴 DSHEA Warning Detected');
    console.log('Element:', element);
    console.table(risks);
    console.warn('⚠️ This is a UI hint only. Server-side validation (rv-webhook) is authoritative.');
    console.warn('📋 Review content before publishing. See FDA DSHEA 21 CFR 101.93 for compliance requirements.');
    console.groupEnd();
  }

  /* ==========================================================================
     ADMIN PREVIEW MODE
     ========================================================================== */

  /**
   * Add admin panel for content authors (Shopify admin preview only)
   */
  if (window.location.search.includes('preview_theme_id')) {
    addAdminPanel();
  }

  function addAdminPanel() {
    const panel = document.createElement('div');
    panel.id = 'dshea-admin-panel';
    panel.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: #fff;
      border: 2px solid #f0ad4e;
      border-radius: 8px;
      padding: 16px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      z-index: 9999;
      max-width: 300px;
    `;

    const warnings = document.querySelectorAll('.dshea-warning').length;

    panel.innerHTML = `
      <h4 style="margin: 0 0 12px 0; font-size: 14px; font-weight: 600;">
        ⚠️ DSHEA Hints (Preview Mode)
      </h4>
      <p style="margin: 0 0 8px 0; font-size: 12px;">
        Detected <strong>${warnings}</strong> potential compliance issue${warnings !== 1 ? 's' : ''}.
      </p>
      <p style="margin: 0; font-size: 11px; color: #666;">
        Highlighted elements require review. Server validation enforced on publish.
      </p>
      <button onclick="document.querySelectorAll('.dshea-warning').forEach(el => el.scrollIntoView({behavior: 'smooth', block: 'center'}))"
              style="margin-top: 12px; width: 100%; padding: 8px; background: #f0ad4e; color: #fff; border: none; border-radius: 4px; cursor: pointer;">
        Scroll to Warnings
      </button>
    `;

    document.body.appendChild(panel);
  }

})();
